/**
 * Hook for fetching and managing user substitutions.
 */

import { gql, useMutation, useQuery } from "@apollo/client";
import type { Substitution } from "@/types";

const SUBSTITUTION_FIELDS = gql`
  fragment SubstitutionFields on SubstitutionType {
    id
    substitutedUserId
    substituteUserId
    startDate
    endDate
    version
    createdAt
    updatedAt
    substitutedUser {
      id
      firstName
      lastName
      email
    }
    substituteUser {
      id
      firstName
      lastName
      email
    }
  }
`;

export const GET_SUBSTITUTIONS = gql`
  query GetSubstitutions($sortBy: String, $sortDir: String) {
    substitutions(sortBy: $sortBy, sortDir: $sortDir) {
      items {
        ...SubstitutionFields
      }
      totalCount
      hasNextPage
    }
  }
  ${SUBSTITUTION_FIELDS}
`;

const CREATE_SUBSTITUTION = gql`
  mutation CreateSubstitution($input: SubstitutionCreateInput!) {
    createSubstitution(input: $input) {
      success
      message
      substitution {
        ...SubstitutionFields
      }
    }
  }
  ${SUBSTITUTION_FIELDS}
`;

const UPDATE_SUBSTITUTION = gql`
  mutation UpdateSubstitution($input: SubstitutionUpdateInput!) {
    updateSubstitution(input: $input) {
      success
      message
      substitution {
        ...SubstitutionFields
      }
    }
  }
  ${SUBSTITUTION_FIELDS}
`;

const DELETE_SUBSTITUTION = gql`
  mutation DeleteSubstitution($id: String!, $version: Int!) {
    deleteSubstitution(id: $id, version: $version) {
      success
      message
    }
  }
`;

interface SubstitutionsQueryResult {
  substitutions: {
    items: Substitution[];
    totalCount: number;
    hasNextPage: boolean;
  };
}

export interface SubstitutionCreateInput {
  substitutedUserId: string;
  substituteUserId: string;
  startDate: string;
  endDate: string;
}

export interface SubstitutionUpdateInput {
  id: string;
  version: number;
  substitutedUserId: string;
  substituteUserId: string;
  startDate: string;
  endDate: string;
}

interface MutationResult {
  success: boolean;
  message?: string | null;
  substitution?: Substitution | null;
}

export function useSubstitutions() {
  const { data, loading, error, refetch } = useQuery<SubstitutionsQueryResult>(
    GET_SUBSTITUTIONS,
    {
      variables: { sortBy: "start_date", sortDir: "desc" },
      fetchPolicy: "cache-and-network",
    },
  );

  const [createMutation] = useMutation(CREATE_SUBSTITUTION, {
    refetchQueries: [{ query: GET_SUBSTITUTIONS, variables: { sortBy: "start_date", sortDir: "desc" } }],
    awaitRefetchQueries: true,
  });

  const [updateMutation] = useMutation(UPDATE_SUBSTITUTION, {
    refetchQueries: [{ query: GET_SUBSTITUTIONS, variables: { sortBy: "start_date", sortDir: "desc" } }],
    awaitRefetchQueries: true,
  });

  const [deleteMutation] = useMutation(DELETE_SUBSTITUTION, {
    refetchQueries: [{ query: GET_SUBSTITUTIONS, variables: { sortBy: "start_date", sortDir: "desc" } }],
    awaitRefetchQueries: true,
  });

  const createSubstitution = async (
    input: SubstitutionCreateInput,
  ): Promise<MutationResult> => {
    const result = await createMutation({ variables: { input } });
    return result.data?.createSubstitution as MutationResult;
  };

  const updateSubstitution = async (
    input: SubstitutionUpdateInput,
  ): Promise<MutationResult> => {
    const result = await updateMutation({ variables: { input } });
    return result.data?.updateSubstitution as MutationResult;
  };

  const deleteSubstitution = async (
    id: string,
    version: number,
  ): Promise<MutationResult> => {
    const result = await deleteMutation({ variables: { id, version } });
    return result.data?.deleteSubstitution as MutationResult;
  };

  return {
    substitutions: (data?.substitutions.items || []) as Substitution[],
    loading,
    error,
    refetch,
    createSubstitution,
    updateSubstitution,
    deleteSubstitution,
  };
}
